import { useState } from 'react'
import type { LabClient } from '@alpha-edge/shared'
import { useApiList } from '../../hooks/use-api-list'
import { useDebounce } from '../../hooks/use-debounce'
import {
  PageHeader,
  DataTable,
  Pagination,
  ColumnSettingsButton,
  StatusBadge,
  btnPrimary,
  input,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { LabClientEditModal } from './LabClientEditModal'
import { useTableColumns } from '../../hooks/use-table-columns'

export function LabClientListPage() {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const { data, total, page, loading, setPage, refresh } = useApiList<LabClient>({
    path: '/lab-clients',
    filters: { search: debouncedSearch || undefined },
  })

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isNew, setIsNew] = useState(false)
  const { tableId: _, ...tableColumnProps } = useTableColumns('lab-clients')

  function openEditModal(client: LabClient) {
    setSelectedId(client.id)
    setIsNew(false)
    setModalOpen(true)
  }

  function openNewModal() {
    setSelectedId(null)
    setIsNew(true)
    setModalOpen(true)
  }

  const columns: readonly Column<LabClient>[] = [
    { key: 'code', header: 'Code', sortable: true, filterable: true, render: (c) => <strong>{c.code}</strong> },
    { key: 'name', header: 'Name', sortable: true, filterable: true, render: (c) => c.name },
    { key: 'prefix', header: 'Barcode Prefix', sortable: true, render: (c) => c.barcodePrefix },
    { key: 'contact', header: 'Contact', render: (c) => c.contactName || '--' },
    { key: 'phone', header: 'Phone', render: (c) => c.contactPhone || '--' },
    {
      key: 'isSystem',
      header: 'System',
      render: (c) => (c.isSystem ? <StatusBadge status="in_progress" label="System" /> : null),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (c) => (c.isActive ? 'Active' : 'Archived'),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Lab Clients"
        subtitle={`${total} total`}
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button type="button" onClick={openNewModal} style={btnPrimary}>
              Add Lab Client
            </button>
          </>
        }
      />
      <div style={{ marginBottom: '1rem' }}>
        <input
          style={{ ...input, width: '320px' }}
          placeholder="Search by code, name, or barcode prefix..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data}
            keyField="id"
            onRowClick={openEditModal}
            emptyMessage="No lab clients found."
            {...tableColumnProps}
          />
          <Pagination page={page} total={total} limit={25} onPageChange={setPage} />
        </>
      )}

      <LabClientEditModal
        labClientId={selectedId}
        isOpen={modalOpen}
        isNew={isNew}
        onClose={() => setModalOpen(false)}
        onSaved={() => refresh()}
        onArchived={() => refresh()}
      />
    </div>
  )
}
